/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: ObjectComponent
 * Reason: ObjectBaseComponent for SmSigningProcess with eight navigation items (Details, Verlauf,
 *         Unterzeichner, Benachrichtigungen, Dokumente, Platzierung, Vor Ort unterschreiben, Audit
 *         Trail), child entity widgets and the two read-only surfaces of A-1/A-2.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {SmSigningApiService} from '../../../service/api/sm-signing-api.service';
import {SmSigningProcessDto} from '../../../model/dto/entity/sm-signing-process.dto';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../../core/shared/logic/object-change-information';
import {DmDocumentService} from '../../../../../feature-core/dm/service/api/dm-document.service';
import {SmAdHocPageDto} from '../../../model/dto/api/sm-ad-hoc-page.dto';
import {
    SM_MANUAL_FIELD_NAME_PREFIX,
    SmManualPlacementDto
} from '../../../model/dto/api/sm-manual-placement.dto';
import {SmPlacementDraft} from '../../../interface/sm-placement-draft.interface';
import {SmPlacementRole} from '../../../interface/sm-placement-role.interface';
import {SmSignerResponseDto} from '../../../model/dto/api/sm-signer-response.dto';
import {SmAdHocSlotDto} from '../../../model/dto/api/sm-ad-hoc-slot.dto';
import {
    SmDispatchResponseDto,
    SmDispatchSignerResultDto,
    SmDispatchOutcomeKind
} from '../../../model/dto/api/sm-dispatch-response.dto';
import {
    smDispatchOutcomeLabel,
    smDispatchOutcomeSeverity
} from '../../../logic/dispatch-outcome/sm-dispatch-outcome-labels';
import {SmSigningProcessStatusEnum} from '../../../model/enum/sm-signing-process-status.enum';
import {forkJoin} from 'rxjs';
import {WidgetDataParam} from '../../../../../core/shared/widget/widget-data-param';
import {WidgetForm} from '../../../../../core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from '../../../../../core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from '../../../../../core/shared/widget/widget-config-form-field';
import {
    smSigningProcessNotificationsQuery,
    SM_SIGNING_PROCESS_NOTIFICATIONS_ALIAS,
    SM_SIGNING_PROCESS_NOTIFICATIONS_COLUMNS,
    SM_SIGNING_PROCESS_NOTIFICATIONS_EMPTY,
    SM_SIGNING_PROCESS_NOTIFICATIONS_ERROR,
    SM_SIGNING_PROCESS_NOTIFICATIONS_HINT
} from '../../../logic/sm-signing-process-notifications-ql';

@Component({
    selector: 'mvs-sm-signing-process-object',
    templateUrl: './sm-signing-process-object.component.html',
    styleUrls: ['./sm-signing-process-object.component.scss'],
    standalone: false
})
export class SmSigningProcessObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    declare dto: SmSigningProcessDto;

    navigationItems: NavigationItem[] = [
        {
            label: 'Details',
            action: 'details',
            tooltip: 'Prozessdetails',
            icon: 'fa-regular fa-info-circle',
            toggleable: false,
            default: true
        },
        {
            // Directly after the details, because it is the tab that answers „was ist hier
            // passiert?“ — the question a caseworker opens a signing process with when something
            // went wrong. „Audit Trail“ stays at the end: that one is the raw hash chain with its
            // sequence numbers, which is evidence rather than reading matter.
            label: 'Verlauf',
            action: 'timeline',
            tooltip: 'Chronologie des Signaturvorgangs',
            icon: 'fa-regular fa-clock-rotate-left',
            toggleable: false,
            default: false
        },
        {
            label: 'Unterzeichner',
            action: 'signers',
            tooltip: 'Unterzeichner',
            icon: 'fa-regular fa-users',
            toggleable: false,
            default: false
        },
        {
            // Beside „Unterzeichner“, because it answers what happened to the invitations that tab
            // sends out.
            label: 'Benachrichtigungen',
            action: 'notifications',
            tooltip: 'Benachrichtigungen an die Unterzeichner',
            icon: 'fa-regular fa-envelope',
            toggleable: false,
            default: false
        },
        {
            label: 'Dokumente',
            action: 'documents',
            tooltip: 'Dokumentversionen',
            icon: 'fa-regular fa-file-lines',
            toggleable: false,
            default: false
        },
        {
            label: 'Platzierung',
            action: 'placement',
            tooltip: 'Unterschriftsfelder von Hand auf dem Dokument platzieren',
            icon: 'fa-regular fa-vector-square',
            toggleable: false,
            default: false
        },
        {
            label: 'Vor Ort unterschreiben',
            action: 'signInPerson',
            tooltip: 'Die offenen Rollen dieses Vorgangs unmittelbar hier unterschreiben lassen',
            icon: 'fa-regular fa-signature',
            toggleable: false,
            default: false
        },
        {
            label: 'Audit Trail',
            action: 'auditTrail',
            tooltip: 'Audit-Protokoll',
            icon: 'fa-regular fa-shield-check',
            toggleable: false,
            default: false
        }
    ];

    // Detail widget
    detailWidget: WidgetData;

    // Child entity widgets
    signerWidget: WidgetData;
    documentVersionWidget: WidgetData;
    auditEventWidget: WidgetData;

    // Action state
    actionBusy: boolean = false;

    // ── Adding a signer (D-2) ───────────────────────────────────────────────────────────────────
    //
    // An own dialog and not the widget's create action. The generic route now exists
    // (/sm/signingProcessSigners) and serves the reads, but it refuses the generic CREATE by name:
    // adding a signer runs an identity capture, a channel-variant admission, an address-origin
    // derivation and an audit entry, and a generic insert would satisfy the database and none of
    // them. Its refusal names the endpoint this dialog writes to.

    /** Whether the add-signer dialog is on screen. */
    signerDialogVisible: boolean = false;

    /** Whether the profile behind the dialog is being resolved. */
    signerDialogOpening: boolean = false;

    /**
     * The signature profile of this process's DOCUMENT TYPE.
     *
     * <p>
     * Read from the signing endpoint and not from the entity: {@code sm.SmSigningProcess} carries no
     * profile column at all — the profile belongs to the document type, and
     * {@code SmSigningProcessResponseDto} derives it. It is what lets the dialog offer the roles the
     * document declares and the channel variants the document type ADMITS, instead of every variant
     * configured in the system.
     * </p>
     */
    signerDialogProfileId: number = null;

    // ── The first dispatch (D-3) ────────────────────────────────────────────────────────────────
    //
    // Not the same thing as the link panel's reissue, and offering only that one was the defect: the
    // reissue REVOKES the credential the customer holds, clears the one-time password lock and moves
    // the deadline. As a first dispatch it works only because there was nothing to destroy yet.

    /** Whether a dispatch is in flight. */
    dispatching: boolean = false;

    /** What the last dispatch reported, per signer. Null until one was run in this view. */
    dispatchResult: SmDispatchResponseDto = null;

    /** The server's own sentence when the whole call was refused. Rendered verbatim. */
    dispatchError: string = null;

    /** The four outcome kinds, for the template — it cannot write the constant itself. */
    readonly outcomeDelivered: string = SmDispatchOutcomeKind.delivered;
    readonly outcomeAlreadyDelivered: string = SmDispatchOutcomeKind.alreadyDelivered;

    // --- Free placement (derivation mode manualPlacement) ---

    /** The source document's bytes. What the placement tool draws on. */
    placementDocument: Blob = null;

    /** Per-page geometry of that document, as the SERVER measured it. */
    placementPages: SmAdHocPageDto[] = [];

    /** The roles a rectangle may be assigned to - the process's own signers, see {@link SmPlacementRole}. */
    placementRoles: SmPlacementRole[] = [];

    /** The rectangles already stored for this process, read back from the layout endpoint. */
    placements: SmPlacementDraft[] = [];

    placementState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
    placementError: string = null;
    placementSaving: boolean = false;

    /** Whether the tool holds changes that have not been sent. */
    placementDirty: boolean = false;

    /** The process the placement session was loaded for. Another id means it has to be loaded again. */
    private placementLoadedFor: number = null;

    // ── Benachrichtigungen (A-1) ────────────────────────────────────────────────────────────────
    //
    // A QL table over ns.NsNotificationChannelRecipient, anchored on the process's SIGNERS. The
    // query needs the signer ids, and those are not on sm.SmSigningProcess — so the tab resolves
    // them from the signing endpoint first and builds its widget only afterwards. Lazy for the same
    // reason the placement tab is: most visits to a process never open it.

    /** The notifications table. Null until the signer ids have been resolved. */
    notificationsWidget: WidgetData;

    notificationsState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';

    /** The process the notifications tab was built for. Another id means it has to be rebuilt. */
    private notificationsLoadedFor: number = null;

    /**
     * Signer name by `sm.SmSigningProcessSigner` id, for the „Unterzeichner“ column.
     *
     * <p>
     * Filled from the SAME read that produces the ids the query filters on, so the column costs no
     * request of its own. See {@link notificationSignerLabel} for why the column needs a map at all.
     * </p>
     */
    private signerNameById: Map<number, string> = new Map<number, string>();

    /**
     * The German pointer the tab must carry: refused dispatches are reported to the CASEWORKER and
     * are therefore anchored on a `um.User`, never on a signer — no filter over the recipient table
     * can reach them, and they appear in the Verlauf as audit events instead. Signed descope 4 of
     * the capability matrix, whose first binding condition is exactly this sentence being visible.
     */
    readonly notificationsHint: string = SM_SIGNING_PROCESS_NOTIFICATIONS_HINT;

    /** Empty state of the tab — the table's own „no data“ text. */
    readonly notificationsEmpty: string = SM_SIGNING_PROCESS_NOTIFICATIONS_EMPTY;

    /** Error state of the tab. */
    readonly notificationsError: string = SM_SIGNING_PROCESS_NOTIFICATIONS_ERROR;

    /**
     * The two columns of the tab that a plain QL cell cannot render, as the widget field ids the
     * custom-field templates in the HTML declare.
     *
     * <p>
     * `#cust_` is {@code MvsWidgetCustomFieldDirective.fieldPrefix}; {@code widget.service.ts:817}
     * recognises a column by that prefix and renders the host's {@code ng-template} for it instead
     * of a form field.
     * </p>
     */
    private static readonly NOTIFICATIONS_SIGNER_FIELD_ID = '#cust_signer';
    private static readonly NOTIFICATIONS_MAIL_FIELD_ID = '#cust_renderedMail';

    private queryParamSubscription: Subscription;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private activatedRoute: ActivatedRoute,
        private signingApiService: SmSigningApiService,
        private dmDocumentService: DmDocumentService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    override onObjectChanged(): void {
        this.setPageTitle(this.dto?.name || 'Signaturprozess');

        // Only set default activeNavigationItems if not already set by query param
        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }

        this.onNavigationItems.emit(this.navigationItems);

        this.createChildWidgets();

        // The object can arrive AFTER the tab was already chosen — through ?navItem, or through a
        // refresh while the placement tab was open. Without this the tab would stay empty until the
        // user switched away and back.
        this.syncPlacementTab();
        this.syncNotificationsTab();
    }

    /**
     * Subscribe to navItem query parameter for direct navigation item switching.
     * Required because ObjectBaseComponent does not subscribe to ?navItem= by default.
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(params => {
            const navItemAction = params['navItem'];
            if (navItemAction && this.navigationItems) {
                const found = this.navigationItems.find(item => item.action === navItemAction);
                if (found) {
                    this.activeNavigationItems = found;
                    // Set directly rather than through the input, so ngOnChanges never sees it and
                    // the lazy tab loads have to be triggered here as well.
                    this.syncPlacementTab();
                    this.syncNotificationsTab();
                }
            }
        });
    }

    /**
     * Create child entity widgets for signers, document versions, and audit events.
     */
    private createChildWidgets(): void {
        const processId = this.objectIdentifier.objectId;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Widget displays SmSigningProcess entity data with process detail fields
         */
        this.detailWidget = WidgetFactory.createWidgetEntityData(
            'sm.signing.process.object.details',
            'Prozessdetails',
            'sm.SmSigningProcess',
            processId
        );

        // Signers table widget with FK text resolution
        const signerFilter = [FilterCriteria.create('e.signingProcessId', FilterCriteria.cOperatorEqual, processId)];
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Widget displays SmSigningProcessSigner table filtered by signing process with FK text resolution, sorted by signing order
         */
        this.signerWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sm.signing.process.object.signers',
            'Unterzeichner',
            'sm.SmSigningProcessSigner',
            signerFilter,
            [new Sorting('e.signingOrder', true)]
        );
        // NO widgetToolbarCreate callback here, deliberately. It opened the platform's metadata form
        // against sm.SmSigningProcessSigner to CREATE, and creation is refused on that route by name:
        // a signer is not a row that can be inserted on its own. Adding one goes through
        // mvs-sm-signer-create-dialog and POST .../signers instead —
        // which is the better route anyway, because it captures the person anchor and DERIVES both
        // address origins from it rather than letting them be typed. The list itself is unaffected;
        // it reads through QL.

        // Document Versions table widget with FK text resolution
        const docFilter = [FilterCriteria.create('e.signingProcessId', FilterCriteria.cOperatorEqual, processId)];
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Widget displays SmDocumentVersion table filtered by signing process with FK text resolution, sorted by version number
         */
        this.documentVersionWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sm.signing.process.object.documents',
            'Dokumentversionen',
            'sm.SmDocumentVersion',
            docFilter,
            [new Sorting('e.versionNumber', true)]
        );

        // Audit Events table widget with FK text resolution
        const auditFilter = [FilterCriteria.create('e.signingProcessId', FilterCriteria.cOperatorEqual, processId)];
        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Widget displays SmAuditEvent table filtered by signing process with FK text resolution, sorted by sequence number
         */
        this.auditEventWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sm.signing.process.object.audit.events',
            'Audit Trail',
            'sm.SmAuditEvent',
            auditFilter,
            [new Sorting('e.sequenceNumber', true)]
        );
    }

    /**
     * Cancel the current signing process via the signing API.
     */
    cancelProcess(): void {
        if (!this.dto?.id) {
            return;
        }

        this.confirmationService.confirm({
            message: 'Möchten Sie diesen Signaturprozess wirklich abbrechen?',
            header: 'Prozess abbrechen',
            icon: 'fa-regular fa-triangle-exclamation',
            accept: () => {
                this.actionBusy = true;
                this.signingApiService.cancelProcess(this.dto.id).subscribe({
                    next: () => {
                        this.actionBusy = false;
                        this.messageService.showSuccessMessage('Erfolg', 'Signaturprozess wurde abgebrochen.');
                        this.refreshObject(false);
                    },
                    error: (err) => {
                        this.actionBusy = false;
                        this.messageService.showErrorMessage('Fehler', 'Prozess konnte nicht abgebrochen werden.');
                    }
                });
            }
        });
    }

    // ============================================================================================
    // Adding a signer (D-2)
    // ============================================================================================

    /**
     * Opens the add-signer dialog, resolving the signature profile first.
     *
     * <p>
     * The profile is fetched HERE and not on every view of the process: it is only ever needed by
     * this dialog, and answering the process endpoint is a request most visits have no use for. It
     * is fetched BEFORE the dialog is shown so that the role list and the variant list are already
     * loading when it appears, rather than the dialog opening empty and filling in afterwards —
     * which is exactly the impression the broken metadata form gave.
     * </p>
     * <p>
     * A failed lookup still opens the dialog. Without a profile the role becomes free text and no
     * channel variant is offered, which is a usable in-person signer and strictly better than a
     * button that does nothing.
     * </p>
     */
    openSignerDialog(): void {

        if (!this.dto?.id || this.signerDialogOpening) {
            return;
        }

        this.signerDialogOpening = true;

        this.signingApiService.getProcess(this.dto.id).subscribe({
            next: (process) => {
                this.signerDialogProfileId = process?.signatureProfileId ?? null;
                this.signerDialogOpening = false;
                this.signerDialogVisible = true;
            },
            error: () => {
                this.signerDialogProfileId = null;
                this.signerDialogOpening = false;
                this.signerDialogVisible = true;
            }
        });
    }

    /**
     * A signer was created, so both the list and everything derived from the signer set have moved.
     *
     * <p>
     * The whole object is refreshed rather than the row appended: the signer widget reads through QL
     * and the placement tab derives its assignable ROLES from the process's signers, so a locally
     * appended row would leave the placement tool offering a role list that is one signer out of
     * date. {@code refreshObject(false)} is the same path the reissue and the cancel already take.
     * </p>
     */
    handleSignerCreated(): void {
        this.messageService.showSuccessMessage('Erfolg', 'Der Unterzeichner wurde angelegt.');

        // The placement session has to be read again for the new role, and syncPlacementTab only
        // reloads when the loaded id changed. Clearing it is what makes the next visit re-read.
        this.placementLoadedFor = null;

        // Same for the notifications tab: its query filters on the signer id LIST, so a tab built
        // before this signer existed would go on excluding everything addressed to them.
        this.notificationsLoadedFor = null;

        this.refreshObject(false);
    }

    // ============================================================================================
    // The first dispatch (D-3)
    // ============================================================================================

    /**
     * Whether the external signing route can still be started for this process.
     *
     * <p>
     * The backend refuses a completed or cancelled process outright, and a button that can only fail
     * is worse than none. An expired process is deliberately still offered: the dispatch extends the
     * deadline, so it is the action that RECOVERS one rather than a call into a certain rejection.
     * </p>
     */
    get canDispatch(): boolean {
        return !!this.dto
            && this.dto.processStatus !== SmSigningProcessStatusEnum.completed
            && this.dto.processStatus !== SmSigningProcessStatusEnum.cancelled;
    }

    /**
     * Starts the external signing route by issuing the first links and sending them out.
     *
     * <h2>Behind a confirmation, because it leaves the building</h2>
     * <p>
     * One click puts a signature request in front of a customer under the tenant's name. That is not
     * undoable and it is not private to Alpha, which is the same bar the cancel and the reissue are
     * held to on this page.
     * </p>
     *
     * <h2>Calling it twice is harmless, and that is a property of the endpoint</h2>
     * <p>
     * {@code linkDeliveredAt} stops a signer being invited a second time, so the button is not
     * disabled after a run. A caseworker who added a signer after the first dispatch has to be able
     * to invite THEM without the previous invitations being reissued underneath the others.
     * </p>
     */
    dispatchProcess(): void {

        if (!this.dto?.id || this.dispatching || !this.canDispatch) {
            return;
        }

        this.confirmationService.confirm({
            message: 'Der Signaturprozess wird jetzt gestartet: Die Einladungen werden ausgestellt '
                + 'und über die hinterlegten Kanalvarianten an die unterzeichnenden Personen '
                + 'zugestellt. Bei sequenzieller Signaturordnung wird zunächst nur die erste Person '
                + 'eingeladen. Möchten Sie den Versand starten?',
            header: 'Versand starten',
            icon: 'fa-regular fa-paper-plane',
            accept: () => this.runDispatch()
        });
    }

    /** The call itself, once the caseworker confirmed. */
    private runDispatch(): void {

        this.dispatching = true;
        this.dispatchError = null;
        this.dispatchResult = null;

        this.signingApiService.dispatchProcess(this.dto.id).subscribe({
            next: (result: SmDispatchResponseDto) => {

                this.dispatching = false;
                this.dispatchResult = result;

                // Reported as a warning where nothing went out. A success toast over a response whose
                // every entry says REFUSED is the one message that would send a caseworker away
                // believing the customer has post.
                if (result?.deliveredCount > 0) {
                    this.messageService.showSuccessMessage('Versand gestartet',
                        result.deliveredCount + ' Einladung(en) wurden zugestellt.');
                } else {
                    this.messageService.showErrorMessage('Versand ohne Zustellung',
                        'Es wurde keine Einladung zugestellt. Die Gründe stehen je Unterzeichner '
                        + 'unter der Aktion.');
                }

                // The link lifecycle is written onto the signers, and the deadline onto the process;
                // both are read from the entity and not from this response.
                this.refreshObject(false);
            },
            error: (err) => {
                this.dispatching = false;
                // The backend answers both of its refusals in German and names which one it was —
                // a terminal process, or no signer that could be invited at all, which is what a
                // process whose signers carry no person anchor looks like. Neither is guessable here.
                this.dispatchError = this.serverMessageOf(err)
                    || 'Der Versand konnte nicht gestartet werden.';
            }
        });
    }

    /** Whether one signer's dispatch put an invitation on its way. */
    dispatchSucceeded(result: SmDispatchSignerResultDto): boolean {
        return result?.outcome === this.outcomeDelivered
            || result?.outcome === this.outcomeAlreadyDelivered;
    }

    /**
     * The German caption of one dispatch outcome.
     *
     * Delegates to the shared mapping: the external signing bench of the playground renders the same
     * four tokens, and two copies of the switch would drift without either of them failing.
     */
    dispatchOutcomeLabel(result: SmDispatchSignerResultDto): string {
        return smDispatchOutcomeLabel(result);
    }

    /** The badge severity of one dispatch outcome. Shared for the same reason as the caption. */
    dispatchOutcomeSeverity(result: SmDispatchSignerResultDto): 'success' | 'warn' | 'danger' {
        return smDispatchOutcomeSeverity(result);
    }

    // ============================================================================================
    // Signing on the spot (D-4)
    // ============================================================================================

    /**
     * A signature was given in the guided flow on this page, so the process has moved on.
     *
     * <p>
     * Refreshed rather than patched: the flow answers with the signer it just wrote, while the status
     * flag in the header, the signer list and the document tab are all read from the process. Leaving
     * the page on the state it held is exactly the stale header the browser run found after signing.
     * </p>
     */
    handleInPersonSignature(): void {
        this.refreshObject(false);
    }

    // ============================================================================================
    // Benachrichtigungen (A-1) — the signer-addressed notifications of this process
    // ============================================================================================

    /**
     * Builds the notifications tab when, and only when, it is actually opened.
     *
     * <p>
     * Lazy for the same reason the placement tab is: the widget cannot be created without the signer
     * ids, the ids cost a read of the signing endpoint, and most visits to a process never open this
     * tab.
     * </p>
     */
    private syncNotificationsTab(): void {

        if (this.activeNavigationItems?.action !== 'notifications') {
            return;
        }

        if (this.notificationsState === 'idle' || this.notificationsLoadedFor !== this.dto?.id) {
            this.loadNotificationsTab();
        }
    }

    /**
     * Resolves the signer ids and builds the table from them.
     *
     * <h3>Why the ids have to be fetched at all</h3>
     * <p>
     * The notifications are anchored polymorphically on the SIGNER — {@code objectType} +
     * {@code objectId} on {@code ns.NsNotificationChannelRecipient} — and {@code sm.SmSigningProcess}
     * carries no signer list. There is no join from the process to the recipient row, so the id list
     * is the filter and it can only come from a read of the signers.
     * </p>
     *
     * <h3>A process with no signers is a READY tab, not an error</h3>
     * <p>
     * The query is built with an empty id list, which yields zero rows, and the table renders its own
     * empty text. Skipping the query would put the tab on a second code path whose loading, empty and
     * error states would then have to be maintained separately — which is exactly how one of the
     * three ends up missing.
     * </p>
     */
    private loadNotificationsTab(): void {

        const processId = this.dto?.id;

        if (!processId) {
            return;
        }

        this.notificationsState = 'loading';
        this.notificationsLoadedFor = processId;

        this.signingApiService.getProcess(processId).subscribe({
            next: (process) => {

                const signers: SmSignerResponseDto[] = process?.signers || [];

                this.signerNameById = new Map<number, string>();
                for (const signer of signers) {
                    // signerName is legitimately EMPTY for a signer whose person was never captured;
                    // the role caption is then the only word that identifies the person in a row.
                    this.signerNameById.set(signer.id, signer.signerName || signer.signerRoleName || '');
                }

                this.notificationsWidget = this.createNotificationsWidget(signers.map(signer => signer.id));
                this.notificationsState = 'ready';
            },
            error: () => {
                this.notificationsState = 'error';
            }
        });
    }

    /** Rebuilds the tab on request — the recovery from a failed read. */
    retryNotificationsLoad(): void {
        this.loadNotificationsTab();
    }

    /**
     * AI:
     * Status: 'in progress'
     * Type: Widget
     * SubType: Runtime
     * Reason: QL table of the signer-addressed notifications of one signing process, over
     *   ns.NsNotificationChannelRecipient, with a curated German column set.
     */
    private createNotificationsWidget(signerIds: number[]): WidgetData {

        const widgetData = WidgetFactory.createWidgetTableQl(
            SM_SIGNING_PROCESS_NOTIFICATIONS_ALIAS,
            'Benachrichtigungen',
            smSigningProcessNotificationsQuery(signerIds),
            WidgetDataParam.create('widgetForm', this.buildNotificationsWidgetForm())
        );

        // „Keine Daten vorhanden“ would be a wider claim than this table can make: nothing was sent
        // TO THE SIGNERS, which is the narrower and honest statement.
        widgetData.noDataText = this.notificationsEmpty;

        return widgetData;
    }

    /**
     * The curated column set, order and German headings of the tab.
     *
     * <p>
     * Order and captions come from {@code SM_SIGNING_PROCESS_NOTIFICATIONS_COLUMNS} rather than being
     * repeated here, so the column list and the QL projections that feed it stay in one file. Two of
     * the eight are swapped for custom-field ids, and only two — see
     * {@link notificationSignerLabel} and {@link openRenderedMail} for what each of them needs that a
     * plain QL cell cannot do.
     * </p>
     * <p>
     * {@code hideFields = true} plus {@code fieldsConfigured = true} is the default-layout convention:
     * without it the generic QL table renders EVERY projected attribute, including the join hops.
     * </p>
     */
    private buildNotificationsWidgetForm(): WidgetForm {

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        SM_SIGNING_PROCESS_NOTIFICATIONS_COLUMNS.forEach((column, index) => {

            let fieldId = column.field;

            if (column.field === 'e.objectId') {
                fieldId = SmSigningProcessObjectComponent.NOTIFICATIONS_SIGNER_FIELD_ID;
            } else if (column.field === 'doc.name') {
                fieldId = SmSigningProcessObjectComponent.NOTIFICATIONS_MAIL_FIELD_ID;
            }

            const field = WidgetConfigFormField.create(fieldId, '', '');
            field.uiLabel = column.uiLabel;
            field.uiPriority = index;
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }

    /**
     * One value of the SOURCE row a custom-field template was handed.
     *
     * <h3>The path is {@code object.row.dto[key]}, and the two obvious ones do not work</h3>
     * <p>
     * Measured in the browser against process 2660, after both columns first shipped BLANK. The
     * mechanism, read back out of the platform afterwards:
     * </p>
     * <ul>
     *   <li>{@code widget.service.ts:442-583} builds one {@code newRecord} per row by looping over
     *       the CONFIGURED columns only — {@code newRecord[formField.absoluteId] = fieldValue} — and
     *       {@code :450-453} {@code continue}s on a custom template field before it ever reads a
     *       value. So {@code object.row} holds exactly the configured, non-custom column ids and
     *       nothing else. Both columns below deliberately REPLACE their QL key with a
     *       {@code #cust_} id, which is precisely what removed {@code e.objectId} and
     *       {@code doc.name} from that record. {@code object.row[key]} was therefore never going to
     *       resolve — the blank cell was the direct consequence of using the seam at all.</li>
     *   <li>{@code object.dto} is {@code handleRow}'s own lookup
     *       ({@code mvs-widget-table.component.ts:988-993}):
     *       {@code this.data.entries.find(item => item[idField] == row[idField])}. For a QL table
     *       the row's id key is the alias-prefixed {@code 'e.id'}, while {@code newRecord['id']} is
     *       only set from {@code entry.id} ({@code widget.service.ts:530}) — which a QL row does not
     *       carry. The match is made on {@code undefined == undefined}, so {@code object.dto} is
     *       unreliable and must not be the primary path.</li>
     * </ul>
     * <p>
     * What IS reliable is {@code newRecord['dto'] = entry} ({@code widget.service.ts:581}): the
     * unmodified QL result row, carrying EVERY projected key, attached to every table entry
     * unconditionally. The table reads it the same way itself
     * ({@code mvs-widget-table.component.ts:753}, {@code entry['dto'][idField]}). Hence
     * {@code object.row.dto} first; the three fallbacks behind it cost nothing and cover the
     * entity-backed table shape, where a configured column DOES sit on {@code row}.
     * </p>
     */
    private static rowValue(object: any, key: string): any {
        return object?.row?.dto?.[key]
            ?? object?.row?.[key]
            ?? object?.dto?.[key]
            ?? object?.[key]
            ?? null;
    }

    /**
     * The caption of the „Unterzeichner“ column.
     *
     * <h3>Why this column needs a template at all — the three seams, measured</h3>
     * <p>
     * {@code ns.NsNotificationChannelRecipient.objectId} is a plain {@code Long} with no relationship
     * behind it, so no QL join can reach {@code sm.SmSigningProcessSigner.signerName} — the compiler
     * has nothing to join on. And a value list supplied by the CALLER never reaches a cell either:
     * {@code widget.service.ts:331-366} clones the BACKEND form field and copies nine properties off
     * the caller's {@code WidgetConfigFormField}, and {@code valueList} is not one of them.
     * </p>
     * <p>
     * Of the two sanctioned seams this is the widget custom-field template — the one that receives
     * the whole row. It deliberately does NOT host {@code mvs-object-inline}, and that is a measured
     * refusal rather than a preference: {@code mvs-object-inline} renders {@code mvs-object} with
     * {@code uiMode="inline"}, and {@code object.component.html:27-29} answers that combination with
     * the literal text „No Inline Implementation Exist“ whenever no object component is registered
     * for the type. {@code sm.SmSigningProcessSigner} has a route in {@code sm.entity-provider.ts}
     * and no {@code objectComponent}, so that sentence — not a name — is what every row of this
     * column would have shown. It would also have cost one {@code cc.ObjectType} lookup plus one
     * entity read PER ROW.
     * </p>
     * <p>
     * The name instead comes from the read this tab already had to make: the signer ids the query
     * filters on and the signer names come out of the same {@code getProcess} response, so the column
     * costs nothing. Where an id is not in that map — a notification anchored on a signer the process
     * no longer lists — the RAW REFERENCE is shown, which is what the two in-tree QL tables over this
     * same entity do ({@code ns-dashboard-worklist.widget.ts}, the {@code epost} worklist, and
     * {@code ns-notification-object.component.ts}).
     * </p>
     */
    notificationSignerLabel(object: any): string {

        const signerId = SmSigningProcessObjectComponent.rowValue(object, 'e.objectId');

        if (signerId == null) {
            return '';
        }

        return this.signerNameById.get(+signerId) || ('#' + signerId);
    }

    /** The caption of the rendered-mail cell: the document's own name, or a dash. */
    notificationMailLabel(object: any): string {
        return SmSigningProcessObjectComponent.rowValue(object, 'doc.name') || 'Schreiben anzeigen';
    }

    /** The `dm.DmDocument` id of a row's rendered mail, or {@code null} when none was stored. */
    notificationMailDocumentId(object: any): number {
        const documentId = SmSigningProcessObjectComponent.rowValue(object, 'doc.id');
        return documentId == null ? null : +documentId;
    }

    /**
     * Opens the rendered mail of one row in a new tab.
     *
     * <h3>Displayed, not downloaded — and through the document service, never a raw URL</h3>
     * <p>
     * {@code GET /dm/dmDocuments/data/{id}/$} is authenticated and tenant-scoped, and a browser
     * NAVIGATION carries neither header — an {@code <a href>} onto it answers 401 in a tab the reader
     * is already looking at, which reads as „das Schreiben ist weg“. {@code openInBrowserTab} fetches
     * the bytes over the authenticated client and puts the blob in the tab, which is the path this
     * object already uses for its documents.
     * </p>
     * <h3>Why {@code text/html}</h3>
     * <p>
     * The mime type is only a FALLBACK: {@code DmDocumentService.showBlobInTab} keeps any type the
     * server sent other than {@code application/octet-stream}, and the DM data endpoint answers with
     * the document's STORED mime type as Content-Type ({@code DmDocumentController}). It applies only
     * when the server sends no usable type. ns no longer stores a rendered body as
     * {@code application/text}: new contents carry the type derived from the content
     * ({@code text/html} for HTML/XHTML mail bodies, {@code text/plain} for plain text —
     * {@code NsNotificationContentService}), and the browser shows them inline. Older documents
     * stored as {@code application/text} keep that type on the wire; no browser renders it inline,
     * so those still open as a download.
     * </p>
     * <p>
     * <b>Limitation, stated rather than hidden:</b> a channel whose content is stored as BINARY (a
     * print letter, for instance) carries its own mime type on the document, and the A-1 query does
     * not project it. Such a row opens as HTML and will look wrong. Projecting
     * {@code dm.DmDocument}'s own mime type is the fix, and it belongs in the query rather than in a
     * guess here.
     * </p>
     */
    openRenderedMail(object: any): void {

        const documentId = this.notificationMailDocumentId(object);

        if (documentId == null) {
            return;
        }

        this.dmDocumentService
            .openInBrowserTab(documentId, 'text/html', this.notificationMailLabel(object))
            .subscribe({
                error: () => this.messageService.showErrorMessage('Fehler',
                    'Das gerenderte Schreiben konnte nicht geöffnet werden.')
            });
    }

    // ============================================================================================
    // Free placement — the host half of the placement tool
    // ============================================================================================

    /**
     * Loads the placement session when, and only when, its tab is actually opened.
     *
     * <p>
     * Deliberately lazy. Answering {@code /layout} costs a document load and a full PDF parse, and
     * the source document is a second download on top of it — which is exactly why the layout is not
     * part of the process response. Paying that on every visit to a process, for a tab most visits
     * never open, would make an unrelated read expensive for everyone.
     * </p>
     */
    private syncPlacementTab(): void {

        if (this.activeNavigationItems?.action !== 'placement') {
            return;
        }

        if (this.placementState === 'idle' || this.placementLoadedFor !== this.dto?.id) {
            this.loadPlacementSession();
        }
    }

    /**
     * Fetches everything the tool needs: the roles, the page geometry, the rectangles that already
     * exist, and the bytes to draw on.
     */
    private loadPlacementSession(): void {

        const processId = this.dto?.id;

        if (!processId) {
            return;
        }

        this.placementState = 'loading';
        this.placementError = null;
        this.placementLoadedFor = processId;

        forkJoin({
            process: this.signingApiService.getProcess(processId),
            layout: this.signingApiService.getProcessLayout(processId)
        }).subscribe({
            next: (result) => {

                this.placementRoles = this.toPlacementRoles(result.process?.signers);
                this.placementPages = result.layout?.pages || [];
                this.placements = this.toPlacementDrafts(result.layout?.slots);
                this.placementDirty = false;

                if (!result.process?.sourceDocumentId) {
                    this.placementState = 'error';
                    this.placementError = 'Der Signaturprozess trägt kein Quelldokument. Ohne '
                        + 'Dokument können keine Unterschriftsfelder platziert werden.';
                    return;
                }

                this.loadPlacementDocument(result.process.sourceDocumentId);
            },
            error: () => {
                this.placementState = 'error';
                this.placementError = 'Die Platzierungsdaten konnten nicht geladen werden. Bitte '
                    + 'versuchen Sie es erneut.';
            }
        });
    }

    /** The source document's bytes, downloaded through dm like every other document read. */
    private loadPlacementDocument(documentId: number): void {

        this.dmDocumentService.download(documentId).subscribe({
            next: (blob) => {
                this.placementDocument = blob;
                this.placementState = 'ready';
            },
            error: () => {
                this.placementState = 'error';
                this.placementError = 'Das Quelldokument konnte nicht geladen werden. Bitte '
                    + 'versuchen Sie es erneut.';
            }
        });
    }

    /** Reloads the placement session on request — the recovery from a failed load. */
    retryPlacementLoad(): void {
        this.loadPlacementSession();
    }

    /**
     * The roles a rectangle may be assigned to: the process's OWN signers.
     *
     * <p>
     * Not the profile's configured roles, although that is where they come from. The write endpoint
     * looks a signer up by role key and refuses the ENTIRE request when the process has none — so a
     * configured role the process has no signer for could only ever produce a rejection that takes
     * every correct rectangle in the same body with it. Deduplicated by key, because two signers may
     * legitimately share a role and the tool addresses the role, not the person.
     * </p>
     */
    private toPlacementRoles(signers: SmSignerResponseDto[]): SmPlacementRole[] {

        const roles: SmPlacementRole[] = [];

        for (const signer of signers || []) {

            if (!signer.signerRole || roles.some(role => role.roleKey === signer.signerRole)) {
                continue;
            }

            // signerRoleName is the caption and signerRole is the key — never show the key, and
            // never send the caption. Falling back to the key is better than an empty select entry.
            roles.push({
                roleKey: signer.signerRole,
                displayName: signer.signerRoleName || signer.signerRole
            });
        }

        return roles;
    }

    /**
     * The rectangles already stored, read back out of the shared layout shape.
     *
     * <p>
     * Only the HAND-PLACED fields, recognised by the synthetic name prefix the server gives them. A
     * field addressed by an AcroForm name belongs to the other derivation mode: it carries no
     * rectangle this tool drew, and offering it for deletion here would let a placement edit remove
     * a signature place the document itself declares.
     * </p>
     */
    private toPlacementDrafts(slots: SmAdHocSlotDto[]): SmPlacementDraft[] {

        const drafts: SmPlacementDraft[] = [];
        let draftId = 1;

        for (const slot of slots || []) {
            for (const field of slot.fields || []) {

                if (!field.rect || !field.page || field.page < 1) {
                    continue;
                }

                if (!field.fieldName?.startsWith(SM_MANUAL_FIELD_NAME_PREFIX)) {
                    continue;
                }

                drafts.push({
                    draftId: draftId++,
                    roleKey: slot.roleKey,
                    page: field.page,
                    rect: field.rect
                });
            }
        }

        return drafts;
    }

    /** Records that the tool holds work nobody has sent yet. */
    onPlacementsChanged(drafts: SmPlacementDraft[]): void {
        this.placementDirty = true;
        this.placements = drafts;
    }

    /**
     * Sends the COMPLETE list and reloads the session from what the server then holds.
     *
     * <p>
     * Reloaded rather than assumed: the server derives the synthetic field names itself and refuses
     * to remove a rectangle somebody has already signed, so what it holds after a save is not always
     * what was sent. Reading it back is the only way the tool and the process cannot drift apart.
     * </p>
     */
    onPlacementsSave(placements: SmManualPlacementDto[]): void {

        const processId = this.dto?.id;

        if (!processId || this.placementSaving) {
            return;
        }

        this.placementSaving = true;

        this.signingApiService.savePlacements(processId, placements).subscribe({
            next: () => {
                this.placementSaving = false;
                this.placementDirty = false;
                this.messageService.showSuccessMessage('Erfolg',
                    'Die Unterschriftsfelder wurden gespeichert.');
                this.loadPlacementSession();
            },
            error: (error) => {
                this.placementSaving = false;
                // The backend answers these refusals in German and names the offending rectangle;
                // replacing that with a generic sentence would throw away the only text that says
                // WHICH rectangle is the problem.
                this.messageService.showErrorMessage('Fehler',
                    this.serverMessageOf(error)
                    || 'Die Unterschriftsfelder konnten nicht gespeichert werden.');
            }
        });
    }

    /** The server's own explanation, where it sent one. */
    private serverMessageOf(error: any): string {

        const body = error?.error;

        if (typeof body === 'string' && body.trim()) {
            return body;
        }

        return typeof body?.message === 'string' && body.message.trim() ? body.message : null;
    }

    /**
     * A signer's link was reissued, so the process state has moved on.
     *
     * <p>
     * Refreshed rather than patched in place: a reissue extends the process deadline and writes the
     * link lifecycle onto the signer, and both are read from the entity rather than from the reissue
     * response. Leaving the page on the state it held would show a deadline that has since moved.
     * </p>
     */
    handleLinkReissued(): void {
        this.refreshObject(false);
    }

    /**
     * Handle object change events from child CRUD operations.
     * Checks event.action before refreshing parent state.
     */
    handleChildChanged(event: ObjectChangeInformation): void {
        if (event.action === ObjectChangeInformationActionEnum.created ||
            event.action === ObjectChangeInformationActionEnum.updated ||
            event.action === ObjectChangeInformationActionEnum.deleted) {
            // Refresh the parent object to pick up any state changes
            this.refreshObject(false);
        }
    }

    /**
     * Returns a human-readable label for the process status.
     */
    getStatusLabel(status: any): string {
        switch (status) {
            case 0: return 'Erstellt';
            case 1: return 'In Bearbeitung';
            case 2: return 'Abgeschlossen';
            case 3: return 'Abgebrochen';
            case 4: return 'Abgelaufen';
            default: return 'Unbekannt';
        }
    }

    /**
     * Returns the severity class for the process status badge.
     */
    getStatusSeverity(status: any): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
        switch (status) {
            case 0: return 'info';
            case 1: return 'warn';
            case 2: return 'success';
            case 3: return 'danger';
            case 4: return 'danger';
            default: return 'secondary';
        }
    }

    /**
     * Returns the signing order label.
     */
    getSigningOrderLabel(order: any): string {
        switch (order) {
            case 0: return 'Sequenziell';
            case 1: return 'Parallel';
            default: return 'Unbekannt';
        }
    }

    override ngOnChanges(changes: SimpleChanges): void {

        super.ngOnChanges(changes);

        // The tab the page switched to. The other path into the same decision is the ?navItem query
        // parameter, which is written straight onto the field and never reaches here.
        if (changes['activeNavigationItems']) {
            this.syncPlacementTab();
            this.syncNotificationsTab();
        }
    }

    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
